import type { ReactNode, MouseEvent } from 'react';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'danger';

export interface ButtonProps {
  readonly children: ReactNode;
  readonly variant?: ButtonVariant;
  readonly onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly type?: 'button' | 'submit' | 'reset';
  readonly className?: string;
  readonly fullWidth?: boolean;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // Primary CTA — theme-aware inverse (text-primary bg + bg fg).
  // Dark theme: white-on-dark. Light theme: brand #1a1d23-on-white.
  primary:
    'bg-text-primary text-bg hover:opacity-90 active:scale-[0.97]',
  // Secondary — filled surface-2 background.
  secondary:
    'bg-surface-2 text-text-primary hover:bg-surface-3 active:scale-[0.97]',
  // Tertiary — text-only link (indigo), no background.
  tertiary:
    'bg-transparent text-link hover:opacity-80 active:scale-[0.97]',
  // Ghost — transparent bg, hover surface-2. Used for inline cancel /
  // modal-close actions where primary/secondary visual weight is too heavy.
  ghost: 'bg-transparent text-text-primary hover:bg-surface-2',
  // Danger — filled neg background for destructive actions (wipe, disable).
  danger: 'bg-neg text-white hover:opacity-90 active:scale-[0.97]',
};

export function Button({
  children,
  variant = 'primary',
  onClick,
  disabled = false,
  loading = false,
  type = 'button',
  className = '',
  fullWidth = false,
}: ButtonProps) {
  const base =
    'inline-flex items-center justify-center px-6 py-3 rounded-xl font-medium ' +
    'transition-all duration-fast ease-press';
  const disabledClass =
    disabled || loading ? 'opacity-50 cursor-not-allowed pointer-events-none' : '';
  const widthClass = fullWidth ? 'w-full' : '';
  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={[base, VARIANT_CLASSES[variant], disabledClass, widthClass, className]
        .filter(Boolean)
        .join(' ')}
    >
      {loading ? '...' : children}
    </button>
  );
}
